import { auth } from '../firebase';
import { MYKYC_API_BASE } from '../config/api';
export const intakeLabels = { givenNames: 'First name', familyName: 'Surname', clientName: 'Client name', clientType: 'Client type', address: 'Address', city: 'City', country: 'Country', dateOfBirth: 'Date of birth (KYC)', incorporationDate: 'Date of incorporation (KYC)', incorporationCountry: 'Country of incorporation', contactPhone: 'Contact phone', contactEmail: 'Contact email', nationality: 'Nationality (KYC)' };
export const categoryLabels = { id_document: 'Passport / National ID', proof_of_address: 'Proof of address', trade_licence: 'Trade licence', cv: 'CV', sow_document: 'Source of wealth document' };
export const sameIntakeValue = (a, b) => String(a || '').normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim() === String(b || '').normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim();
// Compare complete personal names without imposing a given-name / surname order.
export const sameIntakePersonName = (a, b) => {
  const tokens = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/['’]/g, '').split(/[^\p{L}\p{N}]+/u).filter(Boolean).sort();
  const left = tokens(a); const right = tokens(b);
  return left.length > 0 && left.length === right.length && left.every((token, i) => token === right[i]);
};
const kycFields = ['dateOfBirth', 'nationality', 'incorporationDate', 'incorporationCountry'];
const categoryForKycField = {dateOfBirth: 'id_document', nationality: 'id_document', incorporationDate: 'trade_licence', incorporationCountry: 'trade_licence'};
export const titleCaseClientName = value => String(value || '').trim().replace(/\s+/g, ' ')
  .toLowerCase().replace(/(^|[\s'’.-])(\p{L})/gu, (_, separator, letter) => `${separator}${letter.toUpperCase()}`);
const titleCaseLocation = value => String(value || '').trim().replace(/\s+/g, ' ')
  // Keep unit numbers and alphanumeric postal codes exactly as extracted.
  .replace(/[\p{L}\p{N}]+/gu, word => /\p{N}/u.test(word)
    ? word : word.toLowerCase().replace(/^\p{L}/u, letter => letter.toUpperCase()))
  .replace(/\bPo Box\b/g, 'PO Box');
// Printed/OCR full stops are not part of names or cities. For addresses,
// remove stray edge/standalone dots while preserving unit numbers such as 2.5.
const cleanIntakeText = (field, value) => {
  const text = String(value || '');
  if (['clientName', 'city'].includes(field)) return text.replace(/[.．]/g, '').replace(/\s+/g, ' ').trim();
  if (field === 'address') return text.replace(/^[\s.．]+|[\s.．]+$/g, '').replace(/(^|\s)[.．]+(?=\s|$)/g, '$1').replace(/\s+/g, ' ').trim();
  return text;
};
const formatIntakeValue = (field, value) => {
  const cleaned = cleanIntakeText(field, value);
  return field === 'clientName' ? titleCaseClientName(cleaned)
    : ['address', 'city'].includes(field) ? titleCaseLocation(cleaned) : cleaned;
};
export function intakePlan(documents, context, previousDocuments = [], { reviewExisting = false, separateNames = false } = {}) {
  // Last successfully read document of each type wins, both within a drop and
  // across drops. Keep all originals, but do not compare superseded readings.
  const latestByType = new Map();
  [...previousDocuments, ...documents].filter(d => d.status === 'extracted')
    .forEach(d => latestByType.set(d.category, d));
  const incomingIds = new Set(documents.map(d => d.id));
  const active = [...latestByType.values()];
  const extracted = active.filter(d => incomingIds.has(d.id));
  const candidates = field => extracted.flatMap(d => {
    const extractedField = d.extraction?.fields[field];
    if ((['contactPhone','contactEmail'].includes(field) && !['trade_licence', 'cv'].includes(d.category)) || (kycFields.includes(field) && d.category !== categoryForKycField[field] && d.category !== 'sow_document') || !extractedField?.value) return [];
    return [{
      documentId: d.id,
      ...extractedField,
      value: formatIntakeValue(field, extractedField.value),
    }];
  });
  const isEntity = context.clientType === 'entity' || candidates('clientType').some(o => o.value === 'entity');
  const same = (field, a, b) => {
    const left = cleanIntakeText(field, a); const right = cleanIntakeText(field, b);
    return field === 'clientName' && !isEntity ? sameIntakePersonName(left, right) : sameIntakeValue(left, right);
  };
  const conflict = field => {
    const values = active.flatMap(d => {
      if (kycFields.includes(field) && d.category !== categoryForKycField[field] && d.category !== 'sow_document') return [];
      if (['contactPhone','contactEmail'].includes(field) && !['trade_licence', 'cv'].includes(d.category)) return [];
      return d.extraction?.fields[field]?.value || [];
    });
    return values.some(v => !same(field, v, values[0]));
  };
  const ambiguousDocuments = new Set(extracted.filter(d => d.extraction?.multipleSubjects).map(d => d.id));
  const autoFill = {}; const autoKyc = {}; const autoSources = {}; const review = [];
  for (const field of Object.keys(intakeLabels)) {
    if (!separateNames && ["givenNames", "familyName"].includes(field)) continue;
    const options = candidates(field);
    const issues = extracted.flatMap(d => (d.extraction?.issues || []).filter(i => i.field === field).map(i => ({ ...i, documentId: d.id })));
    if (!options.length && !issues.length) continue;
    const uncertain = options.some(o => o.uncertain || o.confidence < 0.85);
    const ambiguous = options.some(o => ambiguousDocuments.has(o.documentId));
    // An equivalent existing value needs no action, even if this scan is less clear.
    if (!ambiguous && !conflict(field) && context[field] && options.length && options.every(o => same(field, o.value, context[field])) && !issues.length) continue;
    // Editing a client requires a choice before replacing any populated value.
    // New-client intake also protects names, including additional given names.
    const existingValueDiffers = (reviewExisting || field === 'clientName') && String(context[field] || '').trim()
      && options.some(option => !same(field, option.value, context[field]));
    if (existingValueDiffers || ambiguous || conflict(field) || uncertain || issues.length) review.push({ field, options, issues, currentValue: context[field] || '', uncertain, existingValueDiffers: !!existingValueDiffers });
    else if (options.length) {
      if (kycFields.includes(field)) autoKyc[field] = { value: options[0].value, documentId: options[0].documentId };
      else {
        autoFill[field] = options[0].value;
        autoSources[field] = options.map(o => o.documentId);
      }
    }
  }
  const identityReview = review.some(row => ['clientName', 'clientType', 'dateOfBirth', 'incorporationDate'].includes(row.field));
  return { autoFill, autoKyc, autoSources, autoPrevious: context, review, identityReview };
}
export async function intakeRequest(action, body, signal, onProgress) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to process documents.');
  const token = await user.getIdToken();
  const response = await fetch(`${MYKYC_API_BASE}/myKyc/clients/document-intake/${action}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(onProgress ? { Accept: 'application/x-ndjson' } : {}) },
    body: JSON.stringify(body), signal,
  });
  let result;
  if (response.ok && response.headers?.get('content-type')?.includes('application/x-ndjson')) {
    const reader = response.body.getReader(); const decoder = new TextDecoder(); let buffer = '';
    const consume = line => {
      if (!line.trim()) return;
      const event = JSON.parse(line);
      if (event.type === 'progress') onProgress?.(event);
      else if (event.type === 'result') result = event.result;
      else if (event.type === 'error') throw new Error(event.message || 'Document processing failed. Please retry.');
    };
    try {
      while (true) {
        const {value, done} = await reader.read();
        buffer += decoder.decode(value, {stream: !done});
        const lines = buffer.split('\n'); buffer = lines.pop(); lines.forEach(consume);
        if (done) break;
      }
      if (buffer.trim()) consume(buffer);
    } finally { reader.releaseLock(); }
    if (!result?.ok) throw new Error('Document processing was interrupted. Please retry.');
    return result;
  }
  try { result = await response.json(); } catch { throw new Error('Document service is unavailable. Please retry.'); }
  if (!response.ok || !result.ok) throw new Error(result.error?.message || 'Document processing failed. Please retry.');
  return result;
}
export function validateIntakeFiles(files) {
  const typeForExtension = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' };
  if (files.length > 5 || files.reduce((sum, f) => sum + f.size, 0) > 20 * 1024 * 1024) throw new Error('Use up to 5 files, totalling no more than 20 MB.');
  return files.map(file => {
    const contentType = file.type || typeForExtension[file.name.split('.').pop().toLowerCase()];
    if (!Object.values(typeForExtension).includes(contentType)) throw new Error('Use PDF, JPEG, PNG or WebP files. Convert other formats first.');
    if (!file.size || file.size > 10 * 1024 * 1024) throw new Error('Each file must be nonempty and no larger than 10 MB.');
    return contentType;
  });
}
export async function prepareIntakeFiles(files) {
  const contentTypes = validateIntakeFiles(files);
  return Promise.all(files.map(async (file, index) => {
    const contentType = contentTypes[index];
    const dataBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader(); reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('This file could not be read.')); reader.readAsDataURL(file);
    });
    return { id: crypto.randomUUID(), name: file.name, contentType, dataBase64, size: file.size };
  }));
}
export function confirmedKycFields(values, clientType) {
  if (clientType === 'entity') {
    const result = {};
    if (/^\d{4}-\d{2}-\d{2}$/.test(values.incorporationDate || '')) result.dobOrIncorporationDate = new Date(`${values.incorporationDate}T00:00:00`);
    if (/^[A-Z]{2}$/.test(values.country || '')) result.nationalityOrIncorporationCountry = values.country;
    return result;
  }
  if (clientType !== 'individual') return {};
  const result = {};
  if (/^\d{4}-\d{2}-\d{2}$/.test(values.dateOfBirth || '')) result.dobOrIncorporationDate = new Date(`${values.dateOfBirth}T00:00:00`);
  if (/^[A-Z]{2}$/.test(values.nationality || '')) result.nationalityOrIncorporationCountry = values.nationality;
  return result;
}

export const cvSourceLabels = { employment: 'Employment', self_employed: 'Self-employed', business_owner: 'Business owner' };
export function confirmedCvFields(profile, clientType) {
  if (clientType !== 'individual' || !profile) return {};
  const supported = new Set((profile.suggestions || []).map(s => s.category));
  return {
    businessActivity: typeof profile.businessActivity === 'string' ? profile.businessActivity.trim() : '',
    sourceOfWealth: [...new Set((profile.selectedSources || []).filter(key => cvSourceLabels[key] && supported.has(key)))],
  };
}

export function intakeReviewValueError(field, value) {
  if (typeof value !== 'string' || value.length > 2000) return 'Enter a shorter value.';
  const v = value.trim();
  if (!v) return '';
  if (['dateOfBirth', 'incorporationDate'].includes(field)) {
    const date = new Date(`${v}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== v || v > new Date().toISOString().slice(0, 10)) return 'Enter a valid date that is not in the future.';
  }
  if (['country', 'nationality', 'incorporationCountry'].includes(field) && !/^[A-Z]{2}$/.test(v)) return 'Choose a country.';
  if (field === 'contactEmail' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address.';
  if (field === 'contactPhone' && (v.replace(/\D/g, '').length < 6 || v.replace(/\D/g, '').length > 15)) return 'Check the phone number.';
  return '';
}
